import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { submitCustomer, confirmOTP, getCustomerForUpdate } from '../../../models/customersModel';
import { getSettings } from '../../../models/settingsModel';

const ID_TYPE_OPTIONS = [
  { value: '2', label: 'South African ID Document' },
  { value: '1', label: 'South African Smart ID Card' },
  { value: '3', label: 'Business Registration Document' },
  { value: '4', label: 'Passport' }
];

const DOCUMENT_UPLOAD_FIELDS = {
  '1': [
    {
      name: 'id_front',
      label: 'Smart ID Card Front',
      description: 'Upload a clear photo of the front side of your Smart ID card.'
    },
    {
      name: 'id_back',
      label: 'Smart ID Card Back',
      description: 'Upload the reverse side of your Smart ID card.'
    }
  ],
  '2': [
    {
      name: 'id_front',
      label: 'ID Document Copy',
      description: 'Upload a high-quality scan or photo of your green ID book.'
    }
  ],
  '3': [
    {
      name: 'id_front',
      label: 'Business Registration Document',
      description: 'Upload your CIPC certificate or equivalent business registration document.'
    }
  ],
  '4': [
    {
      name: 'id_front',
      label: 'Passport Copy',
      description: 'Upload the photo page of your passport.'
    }
  ]
};

const formatTimestamp = (value) => {
  if (!value) {
    return null;
  }

  const numeric = Number(value);
  if (!Number.isNaN(numeric) && Number.isFinite(numeric)) {
    return new Date(numeric * 1000).toLocaleString();
  }

  return value;
};

function CustomerUpdate() {
  const { token } = useParams();
  const [pageLoading, setPageLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmingOtp, setConfirmingOtp] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [customer, setCustomer] = useState(null);
  const [otpRequired, setOtpRequired] = useState(false);
  const [settings, setSettings] = useState(null);
  const [formValues, setFormValues] = useState({
    account: '',
    name: '',
    mobile: '',
    email: '',
    id_type: '2',
    id: '',
    id_front: null,
    id_back: null
  });
  const [otp, setOtp] = useState('');

  const readOnlyInputClass = 'mt-1 block w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-700 shadow-sm focus:outline-none';
  const editableInputClass = 'mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500';
  const fileInputClass = 'mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500';

  const hasCompletedSubmission = useMemo(() => {
    if (!customer) {
      return false;
    }
    return Boolean(customer.submitted && (customer.id_front || customer.id_back));
  }, [customer]);

  const missingDocumentsAfterSubmit = useMemo(() => {
    if (!customer) {
      return false;
    }
    return Boolean(customer.submitted && !customer.id_front && !customer.id_back);
  }, [customer]);

  const hideSensitiveDetails = useMemo(() => {
    if (!customer) {
      return false;
    }

    const { submitted } = customer;

    const hasSubmitted = (() => {
      if (submitted === null || submitted === undefined) {
        return false;
      }

      const numeric = Number(submitted);
      if (!Number.isNaN(numeric)) {
        return numeric > 0;
      }

      return String(submitted).trim() !== '';
    })();

    const processedValue = customer.processed;
    const isProcessed = (() => {
      if (processedValue === null || processedValue === undefined) {
        return false;
      }

      if (typeof processedValue === 'boolean') {
        return processedValue;
      }

      if (typeof processedValue === 'number') {
        return Number(processedValue) === 1;
      }

      const text = String(processedValue).trim().toLowerCase();
      if (text === '') {
        return false;
      }

      return !['0', 'false', 'no', 'pending'].includes(text);
    })();

    return hasSubmitted || isProcessed;
  }, [customer]);

  const loadSettings = async () => {
    try {
      const response = await getSettings();
      if (response && response.status === 1 && response.data) {
        setSettings(response.data);
      }
    } catch (err) {
      console.error('Error loading settings:', err);
    }
  };

  const loadCustomer = async () => {
    if (!token) {
      return;
    }

    setPageLoading(true);
    setError('');

    try {
      const result = await getCustomerForUpdate(token);
      if (result && result.status === 1) {
        const record = result.data || {};
        setCustomer(record);
        setFormValues({
          account: record.account || '',
          name: record.account_name || '',
          mobile: record.new_mobile || record.account_mobile || '',
          email: record.new_email || record.account_email || '',
          id_type: record.id ? String(record.id) : '2',
          id: record.new_id || record.account_id || '',
          id_front: null,
          id_back: null
        });
      } else {
        const fallbackMessage = result && result.message ? result.message : 'Unable to load customer details.';
        const maskDetails = typeof fallbackMessage === 'string' && fallbackMessage.toLowerCase().includes('no longer available');

        if (maskDetails) {
          setCustomer((prev) => {
            if (prev) {
              return {
                ...prev,
                submitted: prev.submitted ?? 1,
                processed: prev.processed ?? 1
              };
            }

            return { submitted: 1 };
          });
        } else {
          setCustomer(null);
          setFormValues((prev) => ({
            ...prev,
            account: '',
            name: '',
            mobile: '',
            email: '',
            id: ''
          }));
        }
        setError(fallbackMessage);
      }
    } catch (err) {
      console.error('Error loading customer details:', err);
      setError('Unable to load customer details.');
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (token) {
      loadCustomer();
    }
  }, [token]);

  const handleChange = (event) => {
    const { name, value, files } = event.target;

    if (files) {
      setFormValues((prev) => ({
        ...prev,
        [name]: files[0]
      }));
      return;
    }

    if (name === 'id_type') {
      setFormValues((prev) => ({
        ...prev,
        id_type: value,
        id_front: null,
        id_back: null
      }));
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const payload = new FormData();
      payload.append('account', formValues.account);
      payload.append('name', formValues.name);
      payload.append('mobile', formValues.mobile);
      payload.append('email', formValues.email);
      payload.append('id_type', formValues.id_type);
      payload.append('id', formValues.id);

      if (formValues.id_front) {
        payload.append('id_file[]', formValues.id_front);
      }

      if (formValues.id_back) {
        payload.append('id_file[]', formValues.id_back);
      }

      const result = await submitCustomer(payload);

      if (result.status === 1) {
        const successMessage = typeof result.data === 'string' && result.data !== ''
          ? result.data
          : result.message || 'Details submitted successfully.';

        setSuccess(successMessage);
        setOtpRequired(true);
        setOtp('');
      } else {
        setError(result.message || 'Failed to submit details.');
      }
    } catch (err) {
      console.error('Error submitting customer details:', err);
      setError('Failed to submit details.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmOtp = async (event) => {
    event.preventDefault();
    setConfirmingOtp(true);
    setError('');
    setSuccess('');

    try {
      const payload = { otp };
      const result = await confirmOTP(payload);

      if (result.status === 1) {
        const successMessage = typeof result.data === 'string' && result.data !== ''
          ? result.data
          : result.message || 'Submission confirmed successfully.';

        setSuccess(successMessage);
        setOtpRequired(false);
        setOtp('');
        setCustomer((prev) => {
          if (!prev) {
            return prev;
          }

          const submittedValue = Number(prev.submitted);
          const alreadyStamped = !Number.isNaN(submittedValue) && submittedValue > 0;

          return alreadyStamped
            ? prev
            : { ...prev, submitted: Math.floor(Date.now() / 1000) };
        });
        await loadCustomer();
      } else {
        setError(result.message || 'Invalid OTP.');
      }
    } catch (err) {
      console.error('Error confirming OTP:', err);
      setError('Invalid OTP.');
    } finally {
      setConfirmingOtp(false);
    }
  };

  const renderDocumentUploads = () => {
    const fields = DOCUMENT_UPLOAD_FIELDS[formValues.id_type] || [];

    if (fields.length === 0) {
      return null;
    }

    return (
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Identification Documents</h3>
        <div className={`grid grid-cols-1 gap-4 ${fields.length > 1 ? 'md:grid-cols-2' : ''}`}>
          {fields.map((field) => (
            <div key={field.name}>
              <label className="block text-sm font-medium text-gray-700">{field.label}</label>
              <input
                type="file"
                name={field.name}
                accept="image/*,.pdf"
                onChange={handleChange}
                className={fileInputClass}
              />
              <p className="mt-1 text-xs text-gray-500">{field.description}</p>
              {formValues[field.name] instanceof File && (
                <p className="mt-1 text-xs text-blue-600">Selected file: {formValues[field.name].name}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const hasDisplayValue = (value) => {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim() !== '';
    }

    return true;
  };

  const formatDisplayValue = (value) => {
    if (value === null || value === undefined) {
      return '';
    }

    const text = String(value).trim();

    if (!text.includes(',')) {
      return text;
    }

    return text
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part !== '')
      .join('\n');
  };

  const renderAccountSummary = () => {
    if (!customer) {
      return null;
    }

    if (hideSensitiveDetails) {
      return null;
    }

    const details = [
      { label: 'Account Number', value: customer.account },
      { label: 'Account Holder', value: customer.account_name },
      { label: 'Registered Mobile', value: customer.account_mobile },
      { label: 'Registered Email', value: customer.account_email }
    ].filter((item) => hasDisplayValue(item.value));

    const pending = [
      { label: 'Pending Mobile Update', value: customer.new_mobile },
      { label: 'Pending Email Update', value: customer.new_email }
    ].filter((item) => hasDisplayValue(item.value));

    const submissionDate = formatTimestamp(customer.submitted);

    const statusChips = [
      { key: 'sent', label: 'Link sent', active: Number(customer.sent) === 1 },
      { key: 'otp', label: 'OTP verified', active: Boolean(customer.submitted) },
      { key: 'processed', label: 'Processed', active: Number(customer.processed) === 1 }
    ];

    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600">Account Summary</h2>
          <div className="flex flex-wrap gap-2">
            {statusChips.map((chip) => (
              <span
                key={chip.key}
                className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${chip.active ? 'border-green-200 bg-green-50 text-green-700' : 'border-gray-200 bg-white text-gray-400'}`}
              >
                {chip.label}
              </span>
            ))}
          </div>
        </div>

        {details.length > 0 && (
          <dl className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            {details.map((item) => (
              <div key={item.label}>
                <dt className="text-xs font-medium text-gray-500">{item.label}</dt>
                <dd className="mt-1 whitespace-pre-line break-words text-sm text-gray-900">{formatDisplayValue(item.value)}</dd>
              </div>
            ))}
          </dl>
        )}

        {pending.length > 0 && (
          <div className="mt-4 rounded-md border border-blue-100 bg-white p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Pending updates</p>
            <dl className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
              {pending.map((item) => (
                <div key={item.label}>
                  <dt className="text-xs font-medium text-blue-600">{item.label}</dt>
                  <dd className="mt-1 whitespace-pre-line break-words text-sm text-blue-900">{formatDisplayValue(item.value)}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {submissionDate && (
          <p className="mt-4 text-xs text-gray-500">Last submitted on {submissionDate}.</p>
        )}
      </div>
    );
  };

  const renderCallouts = () => (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800">
        <p className="font-semibold">Step 1</p>
        <p>Confirm or update your primary contact information.</p>
      </div>
      <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800">
        <p className="font-semibold">Step 2</p>
        <p>Upload the required identification document for your account type.</p>
      </div>
      <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800">
        <p className="font-semibold">Step 3</p>
        <p>Enter the One-Time Pin (OTP) sent to your mobile number or email.</p>
      </div>
    </div>
  );

  const renderMainCard = () => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="px-4 py-5 sm:px-6" style={{ background: 'var(--gradient-primary)' }}>
        <h1 className="text-lg font-semibold text-white">Update your account details</h1>
        <p className="mt-1 text-sm text-white/80">Help us keep your contact information accurate so we can serve you better.</p>
      </div>
      <div className="px-6 py-6 space-y-6">
        {hideSensitiveDetails ? (
          <>
            {success && (
              <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                {success}
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
              This update link has already been confirmed. For your security, account details are no longer displayed. If you need further assistance or wish to make additional changes, please contact Silulumanzi support.
            </div>
          </>
        ) : (
          <>
            {renderAccountSummary()}

            {hasCompletedSubmission && (
              <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                We have received your previous submission and supporting documents. If any details have changed, you can submit updated information below.
              </div>
            )}

            {missingDocumentsAfterSubmit && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                We previously received your submission, but no identification document was attached. Please upload the required files below so we can finalise your update.
              </div>
            )}

            {renderCallouts()}

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {success && (
              <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                {success}
              </div>
            )}

            {otpRequired ? renderOtpForm() : renderForm()}
          </>
        )}

        <div className="border-t border-gray-100 pt-4 text-xs text-gray-500">
          <p>Need help? Call 013&nbsp;752&nbsp;6839 or email enquiries@silulumanzi.com. You can also visit our <a className="text-blue-600 hover:text-blue-700" href="https://portal.silulumanzi.com/portal">Self Service Portal</a>.</p>
        </div>
      </div>
    </div>
  );

  const renderOtpForm = () => (
    <div className="space-y-4">
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
        An OTP has been sent to {formValues.mobile || 'your registered mobile number'} and/or {formValues.email || 'your email address'}.
        Enter the 4-digit code to confirm your submission.
      </div>
      <form onSubmit={handleConfirmOtp} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">One-Time Pin (OTP)</label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={otp}
            onChange={(event) => setOtp(event.target.value)}
            required
            className={editableInputClass}
          />
          <p className="mt-1 text-xs text-gray-500">The OTP expires after a short period for your security.</p>
        </div>
        <button
          type="submit"
          disabled={confirmingOtp}
          className={`w-full inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${confirmingOtp ? 'opacity-80 cursor-not-allowed' : ''}`}
        >
          {confirmingOtp ? 'Confirming…' : 'Confirm OTP'}
        </button>
      </form>
    </div>
  );

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700">Account Number</label>
          <input
            type="text"
            name="account"
            value={formValues.account}
            readOnly
            className={readOnlyInputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Customer Name</label>
          <input
            type="text"
            name="name"
            value={formValues.name}
            onChange={handleChange}
            required
            className={editableInputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Contact Number</label>
          <input
            type="tel"
            name="mobile"
            value={formValues.mobile}
            onChange={handleChange}
            required
            className={editableInputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Email Address</label>
          <input
            type="text"
            name="email"
            value={formValues.email}
            onChange={handleChange}
            required
            className={editableInputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Identity Type</label>
          <select
            name="id_type"
            value={formValues.id_type}
            onChange={handleChange}
            required
            className={editableInputClass}
          >
            {ID_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Identification / Registration Number</label>
          <input
            type="text"
            name="id"
            value={formValues.id}
            onChange={handleChange}
            className={editableInputClass}
          />
        </div>
      </div>

      {renderDocumentUploads()}

      <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
        Attachments may be in PDF, JPG or PNG format (maximum 5&nbsp;MB per file). New uploads will replace any previous documents submitted.
      </div>

      <div>
        <button
          type="submit"
          disabled={submitting}
          className={`w-full inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${submitting ? 'opacity-80 cursor-not-allowed' : ''}`}
        >
          {submitting ? 'Submitting…' : 'Submit Details'}
        </button>
      </div>
    </form>
  );

  const renderHeader = () => (
    <div className="bg-white shadow">
      <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-3">
          {settings?.site_logo_preview ? (
            <img
              src={settings.site_logo_preview}
              alt="Silulumanzi"
              className="h-10 w-auto"
            />
          ) : (
            <span className="text-lg font-semibold text-blue-700">Silulumanzi</span>
          )}
          <span className="hidden text-sm text-gray-500 sm:inline">Customer Profile Update</span>
        </div>
      </div>
    </div>
  );

  const renderLoadingState = () => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="px-6 py-8">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="mt-4 text-sm text-gray-600">Loading customer details…</p>
        </div>
      </div>
    </div>
  );

  const renderErrorState = (message) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="px-6 py-8 text-center">
        <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-2xl font-semibold text-red-600">!</div>
        <h1 className="text-xl font-semibold text-gray-800">Unable to load details</h1>
        <p className="mt-2 text-sm text-gray-600">{message}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }}>
      {renderHeader()}
      <div className="mx-auto max-w-5xl py-6 px-4 sm:px-6 lg:px-8">
        {!token && renderErrorState('This update link appears to be invalid.')}
        {token && pageLoading && !customer && renderLoadingState()}
        {token && !pageLoading && error && !customer && renderErrorState(error)}
        {token && !pageLoading && customer && renderMainCard()}
      </div>
    </div>
  );
}

export default CustomerUpdate;
